import React, { createContext, useState, useMemo, useEffect, useCallback } from 'react';

interface ThemeContextType {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextType>({} as ThemeContextType);

/**
 * Gets the initial theme from localStorage or system preference
 */
function getInitialTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'dark';

  const storedTheme = localStorage.getItem('theme');
  if (storedTheme === 'light' || storedTheme === 'dark') {
    return storedTheme;
  }

  // Check system preference
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }

  return 'dark'; // Default to dark for hive-os style
}

/**
 * Applies theme to the document
 * Uses .dark class on <html> for Tailwind CSS dark mode
 */
function applyTheme(theme: 'light' | 'dark') {
  if (theme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
  // Also keep data-theme for legacy CSS compatibility
  document.documentElement.setAttribute('data-theme', theme);
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = React.memo(({ children }) => {
  const [theme, setTheme] = useState<'light' | 'dark'>(getInitialTheme);

  // Apply theme on mount and when it changes
  useEffect(() => {
    requestAnimationFrame(() => {
      applyTheme(theme);
      localStorage.setItem('theme', theme);
    });
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
  }, []);

  const value = useMemo(() => ({ theme, toggleTheme }), [theme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
});